import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { User } from '../lib/types/core';
import { normalizeProfile } from '../lib/utils/profile';
import { getSelfProfileUpdateKeys } from '@/lib/auth/user-profile';

interface UserProfileValue {
  currentUser: User | null;
  updateProfile: (fields: Parameters<typeof normalizeProfile>[0]) => Promise<void>;
  adminUpdateUserProfile: (targetUserId: string, fields: Partial<User>) => Promise<void>;
  profileStatus: 'loading' | 'active' | 'missing' | 'pending' | 'disabled' | 'conflict_review' | 'password_reset_required' | 'error';
  saving: boolean;
  loading: boolean;
  error: Error | null;
}

const UserProfileContext = createContext<UserProfileValue | null>(null);

export function UserProfileProvider({
  userId,
  children,
}: {
  userId: string;
  children: ReactNode;
}) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [profileStatus, setProfileStatus] = useState<UserProfileValue['profileStatus']>('loading');
  const savingRef = useRef(false);
  const unsubRef = useRef<(() => void) | null>(null);

  // Single permanent onSnapshot with proper cleanup
  useEffect(() => {
    if (!userId) {
      setCurrentUser(null);
      setProfileStatus('missing');
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, 'users', userId);

    const unsubscribe = onSnapshot(
      userDocRef,
      async (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setCurrentUser({
            id: snap.id,
            ...(data as Record<string, unknown>),
            created_at: data.created_at?.toDate?.() ?? data.created_at,
            updated_at: data.updated_at?.toDate?.() ?? data.updated_at,
            last_active: data.last_active?.toDate?.() ?? data.last_active,
            deactivated_at: data.deactivated_at?.toDate?.() ?? data.deactivated_at,
            entertainment_marked_at: data.entertainment_marked_at?.toDate?.() ?? data.entertainment_marked_at,
            entertainment_unmarked_at: data.entertainment_unmarked_at?.toDate?.() ?? data.entertainment_unmarked_at,
          } as unknown as User);
          const status = data.account_status ?? (data.is_active === false ? 'disabled' : 'active');
          setProfileStatus(data.password_reset_required ? 'password_reset_required' : status);
        } else {
          setCurrentUser(null);
          setProfileStatus('missing');
        }
        setLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err : new Error('Firestore listener error'));
        setProfileStatus('error');
        setLoading(false);
      }
    );

    unsubRef.current = unsubscribe;

    return () => {
      unsubscribe();
      unsubRef.current = null;
    };
  }, [userId]);

  const updateProfile = useCallback(
    async (fields: Parameters<typeof normalizeProfile>[0]) => {
      if (savingRef.current) {
        throw new Error('Save already in progress');
      }
      if (!userId) {
        throw new Error('No user ID');
      }

      savingRef.current = true;
      setSaving(true);
      setError(null);

      const normalized = normalizeProfile(fields);
      const allowedKeys = new Set(getSelfProfileUpdateKeys());
      const deniedKey = Object.keys(normalized).find((key) => !allowedKeys.has(key));
      if (deniedKey) {
        throw new Error(`Profile field is not self-editable: ${deniedKey}`);
      }

      // Optimistic update
      setCurrentUser((prev) =>
        prev ? { ...prev, ...normalized } : prev
      );

      try {
        await updateDoc(doc(db, 'users', userId), {
          ...normalized,
          updated_by: userId,
          updated_at: serverTimestamp(),
        });
      } catch (err) {
        // Revert optimistic update on failure
        setCurrentUser((prev) => prev);
        const e = err instanceof Error ? err : new Error('Failed to update profile');
        setError(e);
        throw e;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [userId]
  );

  const adminUpdateUserProfile = useCallback(
    async (targetUserId: string, fields: Partial<User>) => {
      if (!userId) {
        throw new Error('No user ID');
      }
      await updateDoc(doc(db, 'users', targetUserId), {
        ...fields,
        updated_by: userId,
        updated_at: serverTimestamp(),
      });
    },
    [userId],
  );

  return (
    <UserProfileContext.Provider
      value={{ currentUser, updateProfile, adminUpdateUserProfile, profileStatus, saving, loading, error }}
    >
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile(): UserProfileValue {
  const ctx = useContext(UserProfileContext);
  if (!ctx) {
    throw new Error('useUserProfile must be used within UserProfileProvider');
  }
  return ctx;
}
